import type { ComputedRef, Ref } from 'vue'
import type { FetchContext, FetchResponse } from 'ofetch'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import type {
  EventCalendarDay,
  EventCalendarDayMap,
  EventDate,
} from '~/composables/types/event'

dayjs.extend(utc)
dayjs.extend(timezone)

// Extended marker type that supports string arrays for colors
// The @vuepic/vue-datepicker library implementation supports this despite the type definition
interface ExtendedDatePickerMarker {
  date: string
  type?: 'dot' | 'line'
  tooltip?: { text?: string; html?: string; color?: string }[]
  color?: string | string[]
}

export const useMergedCalendarDayMap = (
  eventId: string,
  calendarDayMap?:
    | Ref<EventCalendarDayMap>
    | ComputedRef<EventCalendarDayMap>
    | undefined
) => {
  const mergedCalendaryDayMap = useState<EventCalendarDayMap>(
    `mergedCalendarDayMap-${eventId}`,
    () => ({
      ...(calendarDayMap?.value || {}),
    })
  )

  watchEffect(() => {
    if (calendarDayMap?.value) {
      Object.keys(calendarDayMap.value).forEach((key) => {
        mergedCalendaryDayMap.value[key] = calendarDayMap.value[key]
      })
    }
  })

  return mergedCalendaryDayMap
}

export const useFetchedMonths = (eventId: string) => {
  const fetchedMonths = useState<Record<string, boolean>>(
    `fetchedMonths-${eventId}`,
    () => ({})
  )

  const onResponse = (
    context: FetchContext & { response: FetchResponse<EventCalendarDayMap> }
  ): Promise<void> | void => {
    const { response } = context

    if (response?.status !== 200) return
    const responseUrl = new URL(response?.url)
    const searchParams = responseUrl.searchParams
    const month = searchParams.get('month')
    if (month) {
      fetchedMonths.value[month] = true
    }
  }

  return {
    onResponse,
    fetchedMonths,
  }
}

export const useDatePickerOverlay = () => {
  const isYearOverlayOpen = ref(false)
  const isMonthOverlayOpen = ref(false)

  const isAnyOverlayOpen = computed(
    () => isYearOverlayOpen.value || isMonthOverlayOpen.value
  )

  const onOverlayToggle = (overlay: { overlay: string; open: boolean }) => {
    const map: Record<string, Ref<boolean>> = {
      month: isMonthOverlayOpen,
      year: isYearOverlayOpen,
    }

    if (map[overlay?.overlay]) {
      map[overlay?.overlay].value = overlay.open
    }
  }

  return {
    onOverlayToggle,
    isYearOverlayOpen,
    isAnyOverlayOpen,
    isMonthOverlayOpen,
  }
}

export const buyBoxDatePickerMarkerColors = {
  PUBLIC: 'rgb(134, 163, 21)',
  PRIVATE: 'rgb(2, 97, 102)',
  INSTANT: 'rgba(151, 151, 151, 0.5)',
  red: '#A42515',
}

export const getDateRequestAvailableDays = (eventData: any) => {
  const getMinDateForRequest = () => {
    return dayjs
      .tz(dayjs(), 'UTC')
      .add(
        eventGetters.getDateRequestMinAdvanceQuantity(eventData),
        eventGetters
          .getDateRequestMinAdvanceUnit(eventData)
          .toLowerCase() as any
      )
  }

  const isDateRequestActive = eventGetters.isDateRequestActive(eventData)

  if (!isDateRequestActive) {
    return []
  }

  const availableWeekDays = eventGetters.getDateRequestAvailableDays(eventData)
  const minDateForRequest = getMinDateForRequest()

  // Generate available days for a reasonable range (e.g., 6 months from today)
  const today = dayjs.tz(dayjs(), 'UTC')
  const endDate = today.add(6, 'month')
  const daysCount = endDate.diff(today, 'day') + 1

  return Array.from({ length: daysCount })
    .map((_, index) => today.add(index, 'day'))
    .filter((currentDate) => {
      const dayOfWeek = currentDate.day()

      // Include days that are in available weekdays AND are after min date
      return (
        availableWeekDays.includes(dayOfWeek) &&
        (currentDate.isAfter(minDateForRequest) ||
          currentDate.isSame(minDateForRequest, 'day'))
      )
    })
    .map((currentDate) => currentDate.format('YYYY-MM-DD'))
}

const getInstantBookingEligibleDateStrings = (eventData: any): string[] => {
  if (!eventGetters.isInstantBookingActive(eventData)) {
    return []
  }

  const availableWeekDays = eventGetters.getInstantBookingWeekDays(eventData)
  if (availableWeekDays.length === 0) {
    return []
  }

  const minDateForInstant = dayjs
    .tz(dayjs(), 'UTC')
    .add(
      eventGetters.getInstantBookingMinAdvanceQuantity(eventData),
      eventGetters
        .getInstantBookingMinAdvanceUnit(eventData)
        .toLowerCase() as any
    )

  const today = dayjs.tz(dayjs(), 'UTC')
  const endDate = today.add(6, 'month')
  const daysCount = endDate.diff(today, 'day') + 1

  return Array.from({ length: daysCount })
    .map((_, index) => today.add(index, 'day'))
    .filter((currentDate) => {
      const dayOfWeek = currentDate.day()
      return (
        availableWeekDays.includes(dayOfWeek) &&
        (currentDate.isAfter(minDateForInstant) ||
          currentDate.isSame(minDateForInstant, 'day'))
      )
    })
    .map((currentDate) => currentDate.format('YYYY-MM-DD'))
}

export const useCalendarCellMarkers = (
  calendarDayMap: ComputedRef<EventCalendarDayMap> | Ref<EventCalendarDayMap>,
  selectedTicketsQuantity: ComputedRef<number> | Ref<number>,
  eventData: any,
  allowedDates: ComputedRef<string[]> | Ref<string[]>,
  t: (key: string, ...args: any[]) => string
) => {
  const lightColors = {
    PUBLIC: 'rgb(134, 163, 21, 0.5)',
    PRIVATE: 'rgb(2, 97, 102, 0.5)',
    INSTANT: 'rgba(198, 234, 237, 0.5)',
  }

  const getDateColorsByDateType = (
    dates: EventDate[],
    dateType: 'PUBLIC' | 'PRIVATE'
  ) => {
    const datesFilteredByType = dates.filter((date) =>
      dateType === 'PUBLIC'
        ? eventDateGetters.getIsPublic(date)
        : eventDateGetters.getIsPrivate(date)
    )

    if (datesFilteredByType.length === 0) return undefined

    const maxTickets = Math.max(
      ...datesFilteredByType.map((dateItem) =>
        eventDateGetters.getAvailableTicketsQuantity(dateItem)
      )
    )

    if (selectedTicketsQuantity.value <= maxTickets) {
      return buyBoxDatePickerMarkerColors[dateType]
    } else {
      return lightColors[dateType]
    }
  }

  const getInstantMarkerColorForQuantity = (): string => {
    const minT = eventGetters.getMinInstantBookingTickets(eventData)
    const maxT = eventGetters.getMaxInstantBookingTickets(eventData)
    const qty = selectedTicketsQuantity.value
    if (qty >= minT && qty <= maxT) {
      return buyBoxDatePickerMarkerColors.INSTANT
    }
    return lightColors.INSTANT
  }

  const getColorByCalendarDay = (
    calendarDay: EventCalendarDay,
    instantEligibleSet: Set<string>,
    allowedSet: Set<string>
  ): string[] => {
    if (!calendarDay.tickets_left) {
      return [buyBoxDatePickerMarkerColors.red]
    }

    const publicColors = getDateColorsByDateType(calendarDay.dates, 'PUBLIC')
    const privateColors = getDateColorsByDateType(calendarDay.dates, 'PRIVATE')

    if (publicColors || privateColors) {
      return [publicColors, privateColors].filter((x): x is string =>
        Boolean(x)
      )
    }

    if (
      instantEligibleSet.has(calendarDay.day) &&
      allowedSet.has(calendarDay.day)
    ) {
      return [getInstantMarkerColorForQuantity()]
    }

    return []
  }

  return computed<ExtendedDatePickerMarker[]>(() => {
    const daysInMap = Object.keys(calendarDayMap.value)
    const allowedSet = new Set(allowedDates.value)
    const instantEligibleList = getInstantBookingEligibleDateStrings(eventData)
    const instantEligibleSet = new Set(instantEligibleList)

    const lineMarkers: ExtendedDatePickerMarker[] = Object.values(
      calendarDayMap.value
    )?.map((calendarDay) => {
      const colors = getColorByCalendarDay(
        calendarDay,
        instantEligibleSet,
        allowedSet
      )
      return {
        date: calendarDay.day,
        color: colors,
        type: 'line' as const,
        ...(calendarDay?.tickets_left < 8 && {
          tooltip: [
            {
              text:
                calendarDay.tickets_left > 0
                  ? t(
                      'product.show.checkout.onlyNTicketsLeft',
                      calendarDay?.tickets_left
                    )
                  : t('product.status.soldOut'),
              color: colors[0],
            },
          ],
        }),
      }
    })

    const instantOffCalendarLineMarkers: ExtendedDatePickerMarker[] =
      instantEligibleList
        .filter((dateString) => !daysInMap.includes(dateString))
        .filter((dateString) => allowedSet.has(dateString))
        .map((dateString) => ({
          date: dateString,
          color: [getInstantMarkerColorForQuantity()],
          type: 'line' as const,
        }))

    const dateRequestAvailableDays = getDateRequestAvailableDays(eventData)

    const dotMarkers: ExtendedDatePickerMarker[] = dateRequestAvailableDays
      .filter((dateString) => !daysInMap.includes(dateString))
      .filter((dateString) => !instantEligibleSet.has(dateString))
      .map((dateString) => ({
        date: dateString,
        color: ['rgba(151, 151, 151, 0.5)'], // light gray color
        type: 'dot' as const,
      }))

    // Type assertion needed because @vuepic/vue-datepicker types define color as string
    // but the actual implementation supports string arrays for multiple colors per marker
    return [
      ...lineMarkers,
      ...instantOffCalendarLineMarkers,
      ...dotMarkers,
    ] as any
  })
}
